import { DEFAULT_LOCALE, isSupportedLocale, SUPPORTED_LOCALES, type LocaleCode } from './locales';
import { readPersistedLocale } from './storage';

/**
 * Normalize a browser-supplied tag into our catalog.
 *
 * Strategy:
 *   1. Exact case-insensitive match against a supported code (`pt-BR`).
 *   2. Language-only match (`de-AT` → `de-DE`).
 *   3. null if nothing matches.
 *
 * BCP-47 tags use `-` separators; Traduki file names use `_`. We always
 * normalize to `-` here because that's what `SUPPORTED_LOCALES.code`
 * uses. The underscore form only appears inside dynamic-import paths.
 */
const matchSupported = (tag: string): LocaleCode | null => {
	const normalized = tag.replace('_', '-');

	const exact = SUPPORTED_LOCALES.find((l) => l.code.toLowerCase() === normalized.toLowerCase());
	if (exact) {
		return exact.code;
	}

	const languageOnly = normalized.split('-')[0]?.toLowerCase();
	if (!languageOnly) {
		return null;
	}
	const langMatch = SUPPORTED_LOCALES.find(
		(l) => l.code.toLowerCase().split('-')[0] === languageOnly,
	);
	return langMatch ? langMatch.code : null;
};

const detectFromNavigator = (
	nav: Pick<Navigator, 'language' | 'languages'> | undefined,
): LocaleCode | null => {
	if (!nav) {
		return null;
	}
	const candidates: string[] = [];
	if (Array.isArray(nav.languages)) {
		candidates.push(...nav.languages);
	}
	if (typeof nav.language === 'string') {
		candidates.push(nav.language);
	}
	for (const tag of candidates) {
		const matched = matchSupported(tag);
		if (matched) {
			return matched;
		}
	}
	return null;
};

/**
 * Resolve the locale to use on first render.
 *
 *   1. Persisted (from a previous dropdown choice) — explicit user
 *      intent always wins.
 *   2. Browser preferences (`navigator.languages` / `navigator.language`).
 *   3. `DEFAULT_LOCALE` (`en`).
 *
 * `nav` is injected so the function stays pure and trivially testable
 * in a non-DOM environment.
 */
export const resolveInitialLocale = (
	nav: Pick<Navigator, 'language' | 'languages'> | undefined = typeof navigator !== 'undefined'
		? navigator
		: undefined,
): LocaleCode => {
	const persisted = readPersistedLocale();
	if (isSupportedLocale(persisted)) {
		return persisted;
	}
	const fromBrowser = detectFromNavigator(nav);
	if (fromBrowser) {
		return fromBrowser;
	}
	return DEFAULT_LOCALE;
};
